import React, { useState } from "react";
import { addDays, addMonths, addWeeks, format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { schApi } from "@/lib/schApi";
import { ErrorBox, Loading, btnGold, useLoad } from "@/components/nds/windows/finance/parts";
import { useSchChanged } from "@/components/nds/windows/sch/SchWindows";
import { useAuth } from "@/lib/auth";
import SelectConfirm from "./SelectConfirm";

const STEP = { day: addDays, week: addWeeks, month: addMonths };

function Cell({ c, person, day, on, onTap, id }) {
  const n = c.blocks ?? c.booked.length;
  const tip = c.booked.length ? `${n} block${n === 1 ? "" : "s"} booked · ${c.booked.map((b) => b.title).join(" · ")}` : c.free ? (day ? "Available · tap to select" : "Tap to open this day") : person.on_property ? "Not available" : "Not on property";
  return (
    <button type="button" title={tip} disabled={day && !c.free} onClick={onTap} data-testid={id} aria-pressed={on}
      className={`relative h-7 w-full min-w-[34px] border transition-colors ${on ? "bg-nex-gold border-nex-goldbright ring-2 ring-inset ring-white/70" : `border-black/40 ${c.free ? "bg-emerald-500/70 hover:bg-emerald-400" : "bg-white/10"}`} ${day && !c.free ? "cursor-default" : "cursor-pointer"}`}>
      {n > 0 && <span className={`absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full text-[9px] leading-[14px] font-bold ${c.booked.some((b) => b.work) ? "bg-sky-400 text-black" : "bg-nex-gold text-black"}`}>{c.booked.some((b) => b.work) && c.booked.length === 1 && day ? "W" : n}</span>}
    </button>
  );
}

function Legend() {
  return (
    <span className="ml-auto flex items-center gap-3 text-[10px] text-nex-muted">
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 bg-emerald-500/70" />Available</span>
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 bg-white/10" />Not available</span>
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 bg-nex-gold ring-1 ring-white/70" />Selected</span>
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 rounded-full bg-nex-gold" />Booked</span>
      <span className="inline-flex items-center gap-1"><i className="w-3 h-3 rounded-full bg-sky-400" />Work</span>
    </span>
  );
}

/** Availability: everyone down the side, slots or days across. Day view: tap blocks to select, floating Confirm books them with you. Week/Month: tap a day to open it. */
export default function AvailabilityCard() {
  const [view, setView] = useState("day");
  const [anchor, setAnchor] = useState(new Date());
  const [sel, setSel] = useState({ pid: null, idx: new Set() });
  const { user } = useAuth() || {};
  const clear = () => setSel({ pid: null, idx: new Set() });
  const day = new Date(anchor).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const [g, reload] = useLoad(() => schApi.get("/availability-grid", { view, date: day }), [view, day]);
  useSchChanged(reload);
  const title = view === "month" ? format(anchor, "MMMM yyyy") : view === "week" ? `Week of ${format(addDays(anchor, -((anchor.getDay() + 6) % 7)), "MMM d, yyyy")}` : format(anchor, "EEEE, MMM d, yyyy");
  const d = g.data;
  const isDay = view === "day";
  const tap = (p, i) => {
    if (!isDay) { setAnchor(new Date(d.columns[i].start)); setView("day"); clear(); return; }
    setSel((x) => {
      const idx = new Set(x.pid === p.id ? x.idx : []);
      if (idx.has(i)) idx.delete(i); else idx.add(i);
      return { pid: p.id, idx };
    });
  };
  const selPerson = d && isDay && sel.pid && sel.idx.size ? d.people.find((p) => p.id === sel.pid) : null;
  return (
    <div data-testid="sch-availability" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex border border-white/10 overflow-hidden">
          {["day", "week", "month"].map((v) => (
            <button key={v} type="button" onClick={() => { setView(v); clear(); }} aria-pressed={view === v} data-testid={`avail-view-${v}`}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${view === v ? "bg-nex-gold text-black" : "text-nex-muted hover:text-white"}`}>{v}</button>
          ))}
        </div>
        <button type="button" onClick={() => { setAnchor(new Date()); clear(); }} data-testid="avail-today" className={btnGold}>Today</button>
        <button type="button" onClick={() => { setAnchor((a) => STEP[view](a, -1)); clear(); }} data-testid="avail-prev" className="p-1 text-nex-silver"><ChevronLeft className="h-4 w-4" /></button>
        <button type="button" onClick={() => { setAnchor((a) => STEP[view](a, 1)); clear(); }} data-testid="avail-next" className="p-1 text-nex-silver"><ChevronRight className="h-4 w-4" /></button>
        <span data-testid="avail-title" className="text-sm text-white font-semibold">{title}</span>
        <Legend />
      </div>
      <p data-testid="avail-hint" className="text-[11px] text-nex-muted">{isDay ? "Tap a name, then tap green blocks to select them (tap again to unselect). Confirm books each unbroken run as one session with you." : "Tap any day to open its time blocks."}</p>
      {g.error ? <ErrorBox testid="avail-error" error={g.error} onRetry={reload} /> : !d ? <Loading testid="avail-loading" /> : (
        <div className="overflow-auto max-h-[60vh] border border-white/10" data-testid="avail-grid">
          <table className="border-collapse text-[11px]">
            <thead className="sticky top-0 z-10 bg-[#0b0f1a]">
              <tr><th className="sticky left-0 z-20 bg-[#0b0f1a] text-left px-2 py-1.5 text-nex-muted font-normal min-w-[120px] sm:min-w-[170px]">Person</th>
                {d.columns.map((c) => <th key={c.start} className="px-0.5 py-1.5 text-nex-muted font-normal whitespace-nowrap">{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {d.people.map((p) => (
                <tr key={p.id} data-testid={`avail-row-${p.id}`}>
                  <td onClick={() => isDay && setSel((x) => (x.pid === p.id ? x : { pid: p.id, idx: new Set() }))} data-testid={`avail-name-${p.id}`}
                    className={`sticky left-0 z-[5] px-2 py-0.5 whitespace-nowrap transition-colors ${isDay ? "cursor-pointer" : ""} ${sel.pid === p.id ? "bg-[#2a2210] text-nex-goldbright" : "bg-[#0b0f1a] text-nex-silver"}`}>
                    <span className="inline-block max-w-[96px] sm:max-w-none truncate align-bottom">{p.name}</span> <span className={`ml-1 text-[9px] uppercase tracking-wide ${p.kind === "staff" ? "text-sky-300" : "text-nex-muted"}`}>{p.kind}</span>
                    {!p.on_property && <span className="ml-1 text-[9px] text-amber-300">off property</span>}
                  </td>
                  {p.cells.map((c, i) => <td key={i} className="p-0"><Cell c={c} person={p} day={isDay} on={sel.pid === p.id && sel.idx.has(i)} onTap={() => tap(p, i)} id={`avail-cell-${p.id}-${i}`} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selPerson && <div className="h-16" aria-hidden="true" />}
      {selPerson && <SelectConfirm person={selPerson} idx={sel.idx} columns={d.columns} me={user?.id} onClear={clear} onDone={() => { clear(); reload(); }}
        onBooked={(done) => setSel((x) => ({ ...x, idx: new Set([...x.idx].filter((i) => !done.includes(i))) }))} />}
    </div>
  );
}
